<template>
  <div
    class="select-shell"
    :class="[extraClass, { disabled }]"
    ref="trigger"
  >
    <span v-if="selectedBadge && !search" class="ss-rta-badge-inline" :title="selectedBadgeTitle" :style="{ background: selectedBadgeBg, color: selectedBadgeColor }">{{ selectedBadge }}</span>
    <input
      ref="searchInput"
      class="select-value"
      :class="{ placeholder: !displayValue }"
      :value="displayValue"
      :title="selectedLabel"
      :placeholder="placeholder"
      :readonly="!open"
      :disabled="disabled"
      @input="onInlineInput"
      @click.stop="handleInputClick"
      @focus="handleInputFocus"
    />
    <span v-if="clearable && modelRef != null && !disabled" class="clear-btn" @click.stop="clearSelection" title="Limpar">×</span>
    <span class="arrow" @click.stop="toggleOpen">▾</span>
  </div>
</template>

<script>
export default {
  name: "SearchableSelectRta",
  props: {
    modelRef: {
      type: [String, Number, Object],
      default: null
    },
    optList: {
      type: Array,
      default: () => []
    },
    optValue: {
      type: String,
      default: "value"
    },
    optDisplay: {
      type: String,
      default: "label"
    },
    useIdx: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: "Selecione uma opção"
    },
    inputFunc: {
      type: Function,
      required: true
    },
    searchPlaceholder: {
      type: String,
      default: "Digite para filtrar"
    },
    extraClass: {
      type: [String, Array, Object],
      default: ""
    },
    clearable: {
      type: Boolean,
      default: false
    },
    maxDisplayChars: {
      type: Number,
      default: null
    },
    badgeKey: {
      type: String,
      default: null
    },
  },
  data() {
    return {
      open: false,
      search: "",
      dropdownEl: null,
    };
  },
  computed: {
    normalizedOptions() {
      const limit = this.maxDisplayChars;
      return this.optList.map((opt, idx) => {
        const value = this.useIdx ? idx : opt[this.optValue];
        const display = (opt[this.optDisplay] || "").toString();
        const label = display.length ? display[0].toUpperCase() + display.slice(1) : "";
        const displayLabel = (limit && label.length > limit) ? label.slice(0, limit) + "…" : label;
        const badge = this.badgeKey ? (opt[this.badgeKey] || "") : "";
        const badgeBg = opt.badgeBg || "#eee";
        const badgeColor = opt.badgeColor || "#333";
        const badgeTitle = opt.badgeTitle || "";
        return { value, label, displayLabel, badge, badgeBg, badgeColor, badgeTitle };
      });
    },
    filteredOptions() {
      const term = this.search.toLowerCase();
      if (!term) return this.normalizedOptions;
      return this.normalizedOptions.filter(opt => opt.label.toLowerCase().includes(term));
    },
    selectedOption() {
      return this.normalizedOptions.find(o => o.value === this.modelRef) || null;
    },
    // valor cru selecionado quando não há opção correspondente (ex.: causa renomeada/removida) — evita célula em branco
    fallbackValue() {
      if (this.selectedOption) return "";
      return (typeof this.modelRef === "string" && this.modelRef.trim()) ? this.modelRef : "";
    },
    selectedLabel() {
      return this.selectedOption ? this.selectedOption.label : this.fallbackValue;
    },
    selectedDisplayLabel() {
      if (this.selectedOption) return this.selectedOption.displayLabel;
      const v = this.fallbackValue;
      return (this.maxDisplayChars && v.length > this.maxDisplayChars) ? v.slice(0, this.maxDisplayChars) + "…" : v;
    },
    selectedBadge() {
      return (this.selectedOption && this.selectedOption.badge) ? this.selectedOption.badge : "";
    },
    selectedBadgeBg() {
      return this.selectedOption ? this.selectedOption.badgeBg : "#eee";
    },
    selectedBadgeColor() {
      return this.selectedOption ? this.selectedOption.badgeColor : "#333";
    },
    selectedBadgeTitle() {
      return this.selectedOption ? (this.selectedOption.badgeTitle || "") : "";
    },
    displayValue() {
      if (this.open) return this.search;
      return this.selectedDisplayLabel || "";
    },
  },
  watch: {
    filteredOptions() {
      this.$nextTick(() => this.renderDropdown());
    },
    search() {
      this.$nextTick(() => this.renderDropdown());
    }
  },
  methods: {
    handleInputClick() {
      if (!this.open) {
        this.toggleOpen();
      }
    },
    handleInputFocus() {
      if (!this.open) {
        this.toggleOpen();
      }
    },
    toggleOpen() {
      if (this.disabled) return;
      this.open = !this.open;
      if (this.open) {
        this.$nextTick(() => {
          this.renderDropdown();
          if (this.$refs.searchInput) this.$refs.searchInput.focus();
        });
      } else {
        this.search = "";
        this.removeDropdown();
      }
    },
    getDropdownPosition(contentHeight) {
      const rect = this.$refs.trigger.getBoundingClientRect();
      const maxH = 240;
      const needed = Math.min(contentHeight || maxH, maxH);
      const viewportHeight = (window.visualViewport || { height: window.innerHeight }).height;
      const spaceBelow = viewportHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const margin = 4;

      let top, maxHeight;

      // Se cabe abaixo (considerando a altura real do conteúdo), abre abaixo
      if (spaceBelow >= needed) {
        top = rect.bottom + margin;
        maxHeight = Math.min(spaceBelow, maxH);
      } else if (spaceAbove >= needed) {
        maxHeight = Math.min(spaceAbove, maxH);
        top = rect.top - Math.min(needed, maxHeight) - margin;
      } else if (spaceBelow >= spaceAbove) {
        top = rect.bottom + margin;
        maxHeight = Math.max(spaceBelow, 80);
      } else {
        maxHeight = Math.max(spaceAbove, 80);
        top = rect.top - maxHeight - margin;
      }

      return { top, left: rect.left, width: Math.max(rect.width, 200), maxHeight };
    },
    renderDropdown() {
      if (!this.open || !this.$refs.trigger) return;

      const rect = this.$refs.trigger.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        this.close();
        return;
      }

      if (!this.dropdownEl) {
        this.dropdownEl = document.createElement('div');
        this.dropdownEl.className = 'ss-rta-dropdown';
        document.body.appendChild(this.dropdownEl);
      }

      const el = this.dropdownEl;

      const options = this.filteredOptions;
      const esc = (s) => String(s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
      let html = '';
      if (options.length === 0) {
        html = '<div style="padding:0.35rem;color:#999;font-size:0.85rem;">Sem resultados</div>';
      } else {
        options.forEach((opt, idx) => {
          const isSelected = opt.value === this.modelRef;
          const bgStyle = isSelected ? 'background:#ebf8f8;color:#00A39F;font-weight:600;' : '';
          const badgeHtml = opt.badge ? `<span title="${esc(opt.badgeTitle || '')}" style="display:inline-flex;align-items:center;padding:3px 8px;background:${opt.badgeBg};color:${opt.badgeColor};border-radius:10px;font-size:0.65rem;font-weight:700;line-height:1;letter-spacing:0.02em;white-space:nowrap;flex-shrink:0;">${esc(opt.badge)}</span>` : '';
          html += `<div data-idx="${idx}" data-value="${esc(opt.value)}" title="${esc(opt.label)}" style="display:flex;align-items:center;gap:10px;padding:0.35rem;border-radius:0.15rem;cursor:pointer;font-size:0.85rem;${bgStyle}" class="ss-rta-option">${badgeHtml}<span style="flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${esc(opt.displayLabel)}</span></div>`;
        });
      }
      el.innerHTML = html;

      // Mede a altura real do conteúdo (sem restrição de maxHeight)
      el.style.position = 'fixed';
      el.style.visibility = 'hidden';
      el.style.maxHeight = 'none';
      el.style.overflowY = 'visible';
      el.style.padding = '0.3rem 0';
      el.style.boxSizing = 'border-box';
      const contentHeight = el.scrollHeight;

      // Agora posiciona com base na altura real
      const pos = this.getDropdownPosition(contentHeight);

      el.style.visibility = '';
      el.style.top = pos.top + 'px';
      el.style.left = pos.left + 'px';
      el.style.width = pos.width + 'px';
      el.style.maxHeight = pos.maxHeight + 'px';
      el.style.zIndex = '99999';
      el.style.background = '#fff';
      el.style.border = '1px solid #C7C7C7';
      el.style.borderRadius = '0.2rem';
      el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)';
      el.style.overflowY = 'auto';

      // Scroll to selected
      if (this.modelRef != null) {
        const selectedEl = el.querySelector('.ss-rta-option[style*="background:#ebf8f8"]');
        if (selectedEl) {
          selectedEl.scrollIntoView({ behavior: 'auto', block: 'center' });
        }
      }

      // Remove old listener and add new one
      el.onclick = (e) => {
        const optEl = e.target.closest('.ss-rta-option');
        if (optEl) {
          const idx = parseInt(optEl.dataset.idx);
          const opt = options[idx];
          if (opt) this.selectOption(opt.value);
        }
      };

      // Hover effect
      el.onmouseover = (e) => {
        const optEl = e.target.closest('.ss-rta-option');
        if (optEl && !optEl.style.background.includes('ebf8f8')) {
          optEl.style.background = '#f5f5f5';
        }
      };
      el.onmouseout = (e) => {
        const optEl = e.target.closest('.ss-rta-option');
        if (optEl && !optEl.style.background.includes('ebf8f8')) {
          optEl.style.background = '';
        }
      };
    },
    removeDropdown() {
      if (this.dropdownEl) {
        this.dropdownEl.remove();
        this.dropdownEl = null;
      }
    },
    close() {
      this.open = false;
      this.search = "";
      this.removeDropdown();
      if (this.$refs.searchInput) {
        this.$refs.searchInput.blur();
      }
    },
    onInlineInput(event) {
      this.search = event.target.value;
    },
    clearSelection() {
      this.inputFunc({ target: { value: null } });
      this.close();
    },
    selectOption(val) {
      this.inputFunc({ target: { value: val } });
      setTimeout(() => {
        this.close();
      }, 0);
    },
    handleClickOutside(event) {
      if (!this.$refs.trigger) return;
      const clickedOutsideTrigger = !this.$refs.trigger.contains(event.target);
      const clickedOutsideDropdown = !this.dropdownEl || !this.dropdownEl.contains(event.target);
      if (clickedOutsideTrigger && clickedOutsideDropdown) {
        this.close();
      }
    },
    handleScroll(event) {
      if (!this.open) return;
      const target = event && event.target;
      if (target && typeof target.closest === "function" && target.closest(".ss-rta-dropdown")) return;
      this.renderDropdown();
    }
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    window.addEventListener("scroll", this.handleScroll, true);
    window.addEventListener("resize", this.handleScroll);
    if (typeof IntersectionObserver !== "undefined" && this.$refs.trigger) {
      this.visibilityObserver = new IntersectionObserver((entries) => {
        const e = entries[0];
        // fecha apenas quando o gatilho ficou oculto (ex.: troca de aba), não quando só rolou para fora da viewport
        if (this.open && e && !e.isIntersecting && (!this.$refs.trigger || this.$refs.trigger.getClientRects().length === 0)) {
          this.close();
        }
      }, { threshold: 0 });
      this.visibilityObserver.observe(this.$refs.trigger);
    }
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener("scroll", this.handleScroll, true);
    window.removeEventListener("resize", this.handleScroll);
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
    this.removeDropdown();
  }
};
</script>

<style scoped>
.select-shell {
  position: relative;
  width: 100%;
  height: 100%;
  border: 1px solid #FF5F00;
  border-radius: 0.2rem;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  cursor: pointer;
  box-shadow: none;
}

.select-shell.disabled {
  background: #f5f5f5;
  color: #b3b3b3;
  cursor: not-allowed;
}

.select-shell.disabled .select-value,
.select-shell.disabled .arrow {
  cursor: not-allowed;
}

.select-shell:focus-within {
  box-shadow: none;
}

.select-shell.select-highlight,
.select-shell.input-pentagro {
  border-color: #ff5f00 !important;
}

.select-shell.select-highlight:focus-within,
.select-shell.input-pentagro:focus-within {
  box-shadow: 0 0 0 2px rgba(255, 95, 0, 0.35);
}

.select-shell.select-highlight .select-value,
.select-shell.input-pentagro .select-value {
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  color: #333 !important;
}

.select-value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85rem;
  color: #333;
  border: none !important;
  background: transparent;
  padding: 0;
  outline: none;
  cursor: pointer;
  box-shadow: none !important;
}

.select-value:focus {
  border: none !important;
  box-shadow: none !important;
  outline: none !important;
}

.select-value.placeholder {
  color: #999999;
}

.clear-btn {
  color: #999;
  font-size: 1.1rem;
  cursor: pointer;
  line-height: 1;
  padding: 0 2px;
  transition: color 0.15s;
}

.clear-btn:hover {
  color: #d32f2f;
}

.arrow {
  color: #FF5F00;
  margin-left: 0.4rem;
  font-size: 0.85rem;
  cursor: pointer;
  user-select: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
}

.arrow:hover {
  color: #e55500;
}

.rta-custom-select {
  min-height: 30px;
  height: 30px;
}

.ss-rta-badge-inline {
  display: inline-flex;
  align-items: center;
  margin-right: 0.4rem;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.02em;
  white-space: nowrap;
  flex-shrink: 0;
}
</style>
